import i18n from "@/plugins/vue-i18n";

const DEFAULT_IMAGE = require("@/components/_bit/IconSvg/icons/Image.svg");

function getCatalogPrices(cashPrices, cashlessPrices, paymentMethod) {
  const cashBasicPrice = cashPrices?.basic?.price ?? 0;
  const cashlessBasicPrice = cashlessPrices?.basic?.price ?? 0;
  const cashDealerPrice = cashPrices?.dealer?.price ?? 0;
  const cashlessDealerPrice = cashlessPrices?.dealer?.price ?? 0;
  const promoCashPrice = cashPrices?.promotion?.price ?? 0;
  const promoCashlessPrice = cashlessPrices?.promotion?.price ?? 0;

  if (paymentMethod === "cashless" || paymentMethod === "cash") {
    return {
      priceBasic: {
        title: i18n.t("page.catalog.basicPrice"),
        sum: paymentMethod === "cashless" ? cashlessBasicPrice : cashBasicPrice,
        currencySymbol:
          paymentMethod === "cashless"
            ? cashlessPrices?.basic?.currency?.symbol
            : cashPrices?.basic?.currency?.symbol,
      },
      priceDealer: {
        title: i18n.t("page.catalog.dealerPrice"),
        sum: paymentMethod === "cashless" ? cashlessDealerPrice : cashDealerPrice,
        promotion: paymentMethod === "cashless" ? promoCashlessPrice : promoCashPrice,
        currencySymbol:
          paymentMethod === "cashless"
            ? cashlessPrices?.dealer?.currency?.symbol
            : cashPrices?.dealer?.currency?.symbol,
      },
    };
  }

  return {
    priceBasic: {
      title: i18n.t("page.catalog.basicPrice"),
      sum: paymentMethod === "cashless" ? cashlessBasicPrice : cashBasicPrice,
      currencySymbol:
        paymentMethod === "cashless"
          ? cashlessPrices?.basic?.currency?.symbol
          : cashPrices?.basic?.currency?.symbol,
    },
    priceDealer: {
      title: i18n.t("page.catalog.dealerPrice"),
      sum: paymentMethod === "cash" ? cashDealerPrice : cashlessDealerPrice,
      promotion: paymentMethod === "cash" ? promoCashPrice : promoCashlessPrice,
      currencySymbol:
        paymentMethod === "cash"
          ? cashPrices?.dealer?.currency?.symbol
          : cashlessPrices?.dealer?.currency?.symbol,
    },
  };
}

export function getCartLineId(item) {
  const productId = item.productId ?? item.id ?? item.product?.id;

  if (item.supplyId) {
    return `${productId}:${item.supplyId}:${item.reservationType ?? ""}`;
  }

  return String(productId);
}

export function findCartItemIndex(cartProducts, lineId) {
  return cartProducts.findIndex((item) => getCartLineId(item) === String(lineId));
}

export function mapCartItemToInput(item) {
  const input = {
    productId: item.productId ?? item.id,
    quantity: item.quantity,
  };

  if (item.supplyId) {
    input.supplyId = item.supplyId;
  }

  if (item.reservationType) {
    input.reservationType = item.reservationType;
  }

  if (item.packCount != null) {
    input.packCount = item.packCount;
  }

  return input;
}

export function flattenSupplyGroupItems(supplyGroups = []) {
  return supplyGroups.flatMap(({ supply, items = [] }) =>
    items.map((item) => ({
      ...item,
      supply,
      supplyId: supply?.id,
    }))
  );
}

function buildPromotionList(productPromotion, preOrder) {
  const promo = productPromotion
    ? Array.isArray(productPromotion)
      ? productPromotion.filter(Boolean)
      : [productPromotion]
    : [];

  if (preOrder && !promo.some((item) => item?.type === "preOrder")) {
    promo.push({ type: "preOrder" });
  }

  return promo.length ? promo : null;
}

function mapBaseCartItem(item, { supply, isSupplyItem = false, getPrices } = {}) {
  const {
    quantity,
    product,
    amount,
    isEarlyBird,
    packCount,
    reservationType,
    measureRatio,
    supplyId,
  } = item;

  const {
    id,
    productCode,
    name,
    preOrder,
    hasEarlyBirdSupply,
    cashPrices,
    cashlessPrices,
    promotion: productPromotion,
    imageLinks,
    accessories,
    categories,
  } = product;

  const firstImage = imageLinks?.length ? imageLinks[0] : DEFAULT_IMAGE;
  const catalogPrices = getPrices
    ? getPrices(cashPrices, cashlessPrices)
    : getCatalogPrices(cashPrices, cashlessPrices, "");
  const resolvedSupplyId = supplyId ?? supply?.id ?? null;
  const mappedItem = {
    amount,
    quantity,
    id,
    productCode,
    name,
    preOrder,
    hasEarlyBirdSupply,
    accessories: accessories || [],
    image: firstImage,
    catalogPrices,
    categories,
    promotion: buildPromotionList(productPromotion, preOrder),
    isEarlyBird,
    packCount,
    reservationType,
    measureRatio,
    supplyId: resolvedSupplyId,
    isSupplyItem,
    cartLineId: getCartLineId({ id, supplyId: resolvedSupplyId, reservationType }),
  };

  if (isSupplyItem && supply) {
    mappedItem.deliveryGroup = {
      date: Number(supply.receiptDate),
      warehouse: supply.storage?.name || "",
    };
  }

  return mappedItem;
}

export function mapCartPageItems(cart, getPrices) {
  const inStockItems = (cart?.inStockItems || []).map((item) =>
    mapBaseCartItem(item, { getPrices, isSupplyItem: false })
  );

  const supplyItems = flattenSupplyGroupItems(cart?.supplyGroups).map((item) =>
    mapBaseCartItem(item, {
      supply: item.supply,
      supplyId: item.supplyId,
      isSupplyItem: true,
      getPrices,
    })
  );

  return [...inStockItems, ...supplyItems];
}

export function mapCartPreviewItems(items = [], paymentMethod = "") {
  return items.map((item) => {
    const {
      quantity,
      product,
      amount,
      isEarlyBird,
      packCount,
      reservationType,
      measureRatio,
      supply,
      supplyId,
    } = item;

    const {
      id,
      productCode,
      name,
      preOrder,
      hasEarlyBirdSupply,
      cashPrices,
      cashlessPrices,
      promotion: productPromotion,
      imageLinks,
      categories,
    } = product;

    const firstImage = imageLinks?.length ? imageLinks[0] : DEFAULT_IMAGE;
    const catalogPrices = getCatalogPrices(cashPrices, cashlessPrices, paymentMethod);
    const resolvedSupplyId = supplyId ?? supply?.id ?? null;

    return {
      amount,
      quantity,
      id,
      productCode,
      name,
      image: firstImage,
      catalogPrices,
      categories,
      promotion: productPromotion,
      preOrder,
      hasEarlyBirdSupply,
      isEarlyBird,
      packCount,
      reservationType,
      measureRatio,
      supplyId: resolvedSupplyId,
      isSupplyItem: Boolean(resolvedSupplyId),
      cartLineId: getCartLineId({ id, supplyId: resolvedSupplyId, reservationType }),
      ...(supply
        ? {
            deliveryGroup: {
              date: Number(supply.receiptDate),
              warehouse: supply.storage?.name || "",
            },
          }
        : {}),
    };
  });
}

export function mapCartResponseToPreviewItems(cart, paymentMethod = "") {
  const inStockItems = mapCartPreviewItems(cart?.inStockItems || [], paymentMethod);
  const supplyItems = mapCartPreviewItems(
    flattenSupplyGroupItems(cart?.supplyGroups),
    paymentMethod
  );

  return [...inStockItems, ...supplyItems];
}

export function mapCartBalance(sum) {
  return {
    sum: sum?.value,
    currencySymbol: sum?.currency?.symbol,
  };
}

function normalizePromotion(promotion) {
  if (Array.isArray(promotion)) {
    return promotion.find((item) => item?.type === "discount") || promotion[0] || null;
  }

  return promotion || null;
}

function getCatalogDealerUnitPrice(catalogPrices, promotion) {
  const dealer = catalogPrices?.priceDealer;
  const normalizedPromotion = normalizePromotion(promotion);

  if (!dealer) {
    return { price: 0, currencySymbol: "" };
  }

  const usePromo = normalizedPromotion?.type === "discount" && dealer.promotion;

  return {
    price: usePromo ? dealer.promotion : dealer.sum ?? 0,
    currencySymbol: dealer.currencySymbol || "",
  };
}

export function resolveWholesaleTier(wholesalePrices = [], quantity) {
  const exactMatch = wholesalePrices.find((item) => item.quantity === quantity);

  if (exactMatch) {
    return exactMatch;
  }

  return [...wholesalePrices]
    .filter((item) => item.quantity <= quantity)
    .sort((a, b) => b.quantity - a.quantity)[0];
}

export function getCartModalUnitPrice(product, quantity, paymentMethod) {
  const wholesalePrices = product.wholesalePrices;

  if (wholesalePrices?.length) {
    const tier = resolveWholesaleTier(wholesalePrices, quantity);

    if (tier) {
      const priceBlock = paymentMethod === "cashless" ? tier.cashless : tier.cash;
      const unitPrice = priceBlock?.price ?? 0;

      return {
        unitPrice,
        currency: priceBlock?.currency ?? { symbol: "" },
      };
    }
  }

  const normalizedPromotion = normalizePromotion(product.promotion);
  const { price, currencySymbol } = getCatalogDealerUnitPrice(
    product.catalogPrices,
    normalizedPromotion
  );

  return {
    unitPrice: price,
    currency: { symbol: currencySymbol },
  };
}

export function getWholesaleCartPreviewAmount(
  wholesalePrices,
  quantity,
  paymentMethod,
  product = {}
) {
  const { unitPrice, currency } = getCartModalUnitPrice(
    { ...product, wholesalePrices },
    quantity,
    paymentMethod
  );

  return {
    value: unitPrice * quantity,
    currency,
  };
}

export function mapCatalogProductToCartModalItem(
  product,
  quantity = 1,
  amountOverride = null,
  paymentMethod = ""
) {
  const {
    id,
    name,
    productCode,
    image,
    catalogPrices,
    promotion,
    preOrder,
    categories,
    wholesalePrices,
  } = product;
  const normalizedPromotion = normalizePromotion(promotion);

  let unitPrice;
  let currency;

  if (amountOverride) {
    unitPrice = amountOverride.value / quantity;
    currency = amountOverride.currency ?? { symbol: "" };
  } else if (wholesalePrices?.length) {
    ({ unitPrice, currency } = getCartModalUnitPrice(product, quantity, paymentMethod));
  } else {
    const dealerPrice = getCatalogDealerUnitPrice(catalogPrices, normalizedPromotion);

    unitPrice = dealerPrice.price;
    currency = { symbol: dealerPrice.currencySymbol };
  }

  return {
    id,
    name,
    productCode,
    image: image || DEFAULT_IMAGE,
    quantity,
    unitPrice,
    catalogPrices: catalogPrices || {},
    categories: categories || {},
    promotion: normalizedPromotion,
    preOrder,
    wholesalePrices: wholesalePrices || null,
    remains: product.remains,
    delivery: product.delivery,
    availability: product.availability,
    amount: {
      value: unitPrice * quantity,
      currency,
    },
  };
}

export function getSupplyFreeValue(supply) {
  return Number(supply?.freeValue ?? supply?.free_value ?? 0);
}

export function getDeliverySupplies(product) {
  return product?.delivery?.supplies || [];
}

export function buildCatalogCartSourceProduct(product) {
  return {
    ...product,
    productCode: product.productCode,
    preOrder: product.preOrder,
    remains: product.remains,
    delivery: product.delivery,
    categories: product.categories,
  };
}

export function mapApiProductToCartSource(product, catalogPrices) {
  const firstImage = product.imageLinks?.length ? product.imageLinks[0] : DEFAULT_IMAGE;

  return {
    id: product.id,
    name: product.name,
    productCode: product.productCode,
    image: product.image || firstImage,
    catalogPrices,
    categories: product.categories,
    promotion: product.promotion,
    preOrder: product.preOrder,
    remains: product.remains,
    delivery: product.delivery,
  };
}
